import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { UIMessage } from "@ai-sdk/react";
import {
  useAdsWorkspaceStore,
  type SubAgentChatState,
  type SubAgentId,
  type SerializedUIMessage,
} from "@/features/ads";
import { getAccessToken, resolveApiUrl } from "@/lib/jwt-auth-client";
import { useManagedAgentChat, type UseManagedAgentChatOptions } from "./useManagedAgentChat";
import {
  buildSubAgentSessionPills,
  deserializeSubAgentMessages,
  DRAFT_SUB_AGENT_SESSION_ID,
  serializeSubAgentMessages,
  type AgentSessionMeta,
} from "../lib/subAgentChatSession";

type SubAgentRuntimeTarget = NonNullable<UseManagedAgentChatOptions["runtimeTarget"]>;
const UNIFIED_HISTORY_POLL_INTERVAL_MS = 5_000;
const RUNTIME_SESSION_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const VIDEO_URL_RE = /https?:\/\/[^\s"'<>)]+\.(?:mp4|webm|mov|m4v)(?:\?[^\s"'<>)]*)*/gi;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function collectVideoUrlsFromValue(value: unknown, out: Map<string, string | undefined>): void {
  if (typeof value === "string") {
    let match: RegExpExecArray | null;
    VIDEO_URL_RE.lastIndex = 0;
    while ((match = VIDEO_URL_RE.exec(value)) !== null) {
      out.set(match[0], out.get(match[0]));
    }
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectVideoUrlsFromValue(item, out);
    return;
  }
  if (!isRecord(value)) return;

  const directVideo =
    typeof value.videoUrl === "string"
      ? value.videoUrl
      : typeof value.video_url === "string"
        ? value.video_url
        : undefined;
  const directThumb =
    typeof value.thumbnailUrl === "string"
      ? value.thumbnailUrl
      : typeof value.thumbnail_url === "string"
        ? value.thumbnail_url
        : undefined;
  if (directVideo) out.set(directVideo, directThumb);
  for (const child of Object.values(value)) collectVideoUrlsFromValue(child, out);
}

function collectVideoUrlsFromMessages(messages: UIMessage[]): Array<{
  videoUrl: string;
  thumbnailUrl?: string;
}> {
  const urls = new Map<string, string | undefined>();
  for (const message of messages) {
    if (message.role !== "assistant") continue;
    for (const part of message.parts ?? []) {
      collectVideoUrlsFromValue(part, urls);
    }
  }
  return [...urls.entries()].map(([videoUrl, thumbnailUrl]) => ({
    videoUrl,
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
  }));
}

export function resolveSubAgentChatRuntime(args: {
  agentId: SubAgentId;
  sessionId: string | null;
  runtimeTarget?: UseManagedAgentChatOptions["runtimeTarget"];
  agentName?: string;
}): { runtimeTarget: SubAgentRuntimeTarget; agentName?: string } {
  const runtimeTarget = args.runtimeTarget ?? "anthropic_sdk";
  if (args.agentId === "banan" && args.sessionId && RUNTIME_SESSION_ID_RE.test(args.sessionId)) {
    return { runtimeTarget: "unified", agentName: "banan_renderer" };
  }
  return {
    runtimeTarget,
    ...(args.agentName ? { agentName: args.agentName } : {}),
  };
}

export async function fetchSessionHistory(
  sessionId: string,
  runtimeTarget: SubAgentRuntimeTarget = "anthropic_sdk",
): Promise<SerializedUIMessage[]> {
  try {
    const token = await getAccessToken();
    const endpoint =
      runtimeTarget === "unified"
        ? `/core/agent/runtime-sessions/${sessionId}/history`
        : `/core/agent/managed-chat/history/${sessionId}`;
    const res = await fetch(resolveApiUrl(endpoint), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return [];
    const data = await res.json();
    const messages = data?.messages ?? data;
    return Array.isArray(messages) ? messages : [];
  } catch {
    return [];
  }
}

export async function fetchAgentSessions(
  agentName: string,
  workspaceId: string | null,
  runtimeTarget: SubAgentRuntimeTarget = "anthropic_sdk",
): Promise<AgentSessionMeta[]> {
  // No workspace = nothing to scope against. Return empty rather than
  // leaking every session the agent has ever had across all workspaces.
  if (!workspaceId) return [];
  try {
    const token = await getAccessToken();
    if (runtimeTarget === "unified") {
      const qs = new URLSearchParams({ agentName, limit: "50" });
      const res = await fetch(
        resolveApiUrl(
          `/core/agent/workspace/${encodeURIComponent(workspaceId)}/sessions?${qs.toString()}`,
        ),
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!res.ok) return [];
      const data = await res.json();
      const sessions = Array.isArray(data?.sessions) ? data.sessions : [];
      return sessions.map((session: Record<string, unknown>) => ({
        id: typeof session.id === "string" ? session.id : "",
        title: typeof session.title === "string" && session.title.length > 0 ? session.title : null,
        status: typeof session.status === "string" ? session.status : "idle",
        createdAt:
          typeof session.createdAt === "string" && session.createdAt.length > 0
            ? session.createdAt
            : new Date().toISOString(),
        assetKind: null,
      })).filter((session: AgentSessionMeta) => session.id.length > 0);
    }

    const qs = new URLSearchParams({ agentId: agentName, workspaceId });
    const res = await fetch(resolveApiUrl(`/core/agent/managed-chat/sessions?${qs.toString()}`), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.sessions) ? data.sessions : [];
  } catch {
    return [];
  }
}

interface UseSubAgentManagedChatSessionOptions {
  agentId: SubAgentId;
  backendAgentId: string;
  runtimeTarget?: UseManagedAgentChatOptions["runtimeTarget"];
  agentName?: string;
  chatState: SubAgentChatState;
  onImageGenerated?: UseManagedAgentChatOptions["onImageGenerated"];
  onVideoGenerated?: UseManagedAgentChatOptions["onVideoGenerated"];
  onToolUse?: UseManagedAgentChatOptions["onToolUse"];
}

export function useSubAgentManagedChatSession({
  agentId,
  backendAgentId,
  runtimeTarget = "anthropic_sdk",
  agentName,
  chatState,
  onImageGenerated,
  onVideoGenerated,
  onToolUse,
}: UseSubAgentManagedChatSessionOptions) {
  const setSubAgentSessionId = useAdsWorkspaceStore((s) => s.setSubAgentSessionId);
  const setSubAgentSessionCreatedAt = useAdsWorkspaceStore((s) => s.setSubAgentSessionCreatedAt);
  const cacheSubAgentMessages = useAdsWorkspaceStore((s) => s.cacheSubAgentMessages);
  const scopeWorkspaceId = useAdsWorkspaceStore((s) => s.scopeWorkspaceId);
  const setSubAgentSessionList = useAdsWorkspaceStore((s) => s.setSubAgentSessionList);
  const cachedSessionList = useAdsWorkspaceStore((s) => s.subAgentSessionList[agentId]);
  const [isRestoringHistory, setIsRestoringHistory] = useState(false);
  const [sessionList, setSessionList] = useState<AgentSessionMeta[]>(() => cachedSessionList);
  const sessionListAgentRef = useRef(agentId);
  const cachedSessionListRef = useRef(cachedSessionList);
  const historyFetchedForRef = useRef<string | null>(null);
  const historyPollCacheKeyRef = useRef<string | null>(null);
  const historyVideoUrlsNotifiedRef = useRef<Set<string>>(new Set());
  const resolvedChatRuntime = useMemo(
    () =>
      resolveSubAgentChatRuntime({
        agentId,
        sessionId: chatState.sessionId,
        runtimeTarget,
        agentName,
      }),
    [agentId, chatState.sessionId, runtimeTarget, agentName],
  );
  const resolvedRuntimeTarget = resolvedChatRuntime.runtimeTarget;
  const resolvedAgentName = resolvedChatRuntime.agentName;

  const initialMessages = useMemo(
    () =>
      chatState.cachedMessages.length > 0
        ? deserializeSubAgentMessages(chatState.cachedMessages)
        : undefined,
    [chatState.cachedMessages],
  );

  const chat = useManagedAgentChat({
    agentId: backendAgentId,
    runtimeTarget: resolvedRuntimeTarget,
    agentName:
      resolvedRuntimeTarget === "unified" ? resolvedAgentName ?? backendAgentId : undefined,
    workspaceId: scopeWorkspaceId,
    initialSessionId: chatState.sessionId,
    initialMessages,
    onImageGenerated,
    onVideoGenerated,
    onToolUse,
  });

  const { messages, setMessages, sessionId, status } = chat;
  const isLoading = status === "submitted" || status === "streaming";

  const notifyHistoryVideos = useCallback(
    (uiMessages: UIMessage[]) => {
      if (!onVideoGenerated) return;
      for (const video of collectVideoUrlsFromMessages(uiMessages)) {
        if (historyVideoUrlsNotifiedRef.current.has(video.videoUrl)) continue;
        historyVideoUrlsNotifiedRef.current.add(video.videoUrl);
        onVideoGenerated(video.videoUrl, video.thumbnailUrl);
      }
    },
    [onVideoGenerated],
  );

  // The session ID the hook is currently driving — always matches the pill
  // the user has selected. Clicking a different pill calls
  // setSubAgentSessionId, which the hook picks up as initialSessionId and
  // reinitializes against.
  const liveSessionId = sessionId ?? chatState.sessionId;

  useEffect(() => {
    if (resolvedRuntimeTarget !== "unified") return;
    if (!liveSessionId) return;
    if (isLoading) return;

    let cancelled = false;
    const syncHistory = async () => {
      const history = await fetchSessionHistory(liveSessionId, resolvedRuntimeTarget);
      if (cancelled || history.length === 0) return;
      const cacheKey = `${liveSessionId}:${JSON.stringify(history)}`;
      if (historyPollCacheKeyRef.current === cacheKey) return;
      historyPollCacheKeyRef.current = cacheKey;
      const deserialized = deserializeSubAgentMessages(history);
      setMessages(deserialized);
      notifyHistoryVideos(deserialized);
      cacheSubAgentMessages(agentId, history);
    };

    void syncHistory();
    const intervalId = window.setInterval(syncHistory, UNIFIED_HISTORY_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    agentId,
    liveSessionId,
    resolvedRuntimeTarget,
    isLoading,
    setMessages,
    cacheSubAgentMessages,
    notifyHistoryVideos,
  ]);

  // Sync a newly-created sessionId back to the store (one-way: hook -> store).
  useEffect(() => {
    if (sessionId && !chatState.sessionId) {
      setSubAgentSessionId(agentId, sessionId);
    }
  }, [sessionId, agentId, chatState.sessionId, setSubAgentSessionId]);

  // Restore chat history from the canonical server events when entering a
  // session cold with no cached messages and no live replayed messages.
  useEffect(() => {
    const sid = chatState.sessionId;
    if (!sid) return;
    if (historyFetchedForRef.current === sid) return;
    if (chatState.cachedMessages.length > 0 || messages.length > 0) return;

    historyFetchedForRef.current = sid;
    setIsRestoringHistory(true);
    fetchSessionHistory(sid, resolvedRuntimeTarget)
      .then((history) => {
        if (historyFetchedForRef.current !== sid) return;
        if (history.length === 0) return;
        const deserialized = deserializeSubAgentMessages(history);
        setMessages((current) => (current.length === 0 ? deserialized : current));
        notifyHistoryVideos(deserialized);
        cacheSubAgentMessages(agentId, history);
      })
      .finally(() => {
        if (historyFetchedForRef.current === sid) {
          setIsRestoringHistory(false);
        }
      });
  }, [
    chatState.sessionId,
    chatState.cachedMessages.length,
    messages.length,
    agentId,
    setMessages,
    cacheSubAgentMessages,
    notifyHistoryVideos,
    resolvedRuntimeTarget,
  ]);

  useEffect(() => {
    cachedSessionListRef.current = cachedSessionList;
  }, [cachedSessionList]);

  useEffect(() => {
    if (sessionListAgentRef.current !== agentId) {
      sessionListAgentRef.current = agentId;
      setSessionList(cachedSessionList);
    }
  }, [agentId, cachedSessionList]);

  useEffect(() => {
    let cancelled = false;
    setSessionList((current) => (current.length > 0 ? current : cachedSessionListRef.current));
    fetchAgentSessions(
      resolvedRuntimeTarget === "unified" ? resolvedAgentName ?? backendAgentId : backendAgentId,
      scopeWorkspaceId,
      resolvedRuntimeTarget,
    ).then((sessions) => {
      if (cancelled) return;
      setSessionList(sessions);
    });
    return () => {
      cancelled = true;
    };
  }, [backendAgentId, resolvedAgentName, resolvedRuntimeTarget, scopeWorkspaceId]);

  useEffect(() => {
    if (!liveSessionId) return;
    setSessionList((prev) => {
      if (prev.some((session) => session.id === liveSessionId)) return prev;
      return [
        {
          id: liveSessionId,
          title: null,
          status: "idle",
          createdAt: new Date().toISOString(),
          assetKind: null,
        },
        ...prev,
      ];
    });
  }, [liveSessionId]);

  useEffect(() => {
    if (!liveSessionId) return;
    const session = sessionList.find((item) => item.id === liveSessionId);
    if (session) setSubAgentSessionCreatedAt(agentId, session.createdAt);
  }, [liveSessionId, sessionList, agentId, setSubAgentSessionCreatedAt]);

  useEffect(() => {
    setSubAgentSessionList(agentId, sessionList);
  }, [sessionList, agentId, setSubAgentSessionList]);

  const handleSelectSession = useCallback(
    (selectedId: string) => {
      if (selectedId === DRAFT_SUB_AGENT_SESSION_ID) return;
      if (selectedId === liveSessionId) return;
      cacheSubAgentMessages(agentId, []);
      setSubAgentSessionId(agentId, selectedId);
    },
    [liveSessionId, agentId, cacheSubAgentMessages, setSubAgentSessionId],
  );

  const sessionPills = useMemo(
    () => buildSubAgentSessionPills({ sessionList, liveSessionId }),
    [sessionList, liveSessionId],
  );

  const messagesRef = useRef<UIMessage[]>(messages);
  messagesRef.current = messages;

  useEffect(() => {
    return () => {
      const currentMessages = messagesRef.current;
      if (currentMessages.length > 0) {
        cacheSubAgentMessages(agentId, serializeSubAgentMessages(currentMessages));
      }
    };
  }, [agentId, cacheSubAgentMessages]);

  return {
    ...chat,
    activePillSessionId: liveSessionId ?? DRAFT_SUB_AGENT_SESSION_ID,
    isLoading,
    isRestoringHistory,
    liveSessionId,
    sessionPills,
    handleSelectSession,
  };
}
